<template>
  <!-- Add Manual Activity Modal -->
  <div :id="id" class="modal custom-modal fade" role="dialog">
    <div class="modal-dialog modal-dialog-centered modal-md" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Add Activity</h5>
          <button type="button" ref="Close" class="btn-close" data-bs-dismiss="modal" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submit()">
            <!-- Employee Selection -->
            <div class="input-block mb-3">
              <label class="col-form-label">Employee <span class="text-danger">*</span></label>
              <v-autocomplete 
                v-model="selectedEmployee" 
                :items="employeeOptions" 
                
                variant="outlined"
                density="comfortable"
                item-title="text" 
                item-value="id"
                clearable
              ></v-autocomplete>
            </div>

            <!-- Activity Type -->
            <div class="input-block mb-3">
              <label class="col-form-label">Activity Type <span class="text-danger">*</span></label>
              <v-select
                v-model="activityType"
                :items="activityTypeOptions"
                
                variant="outlined"
                density="comfortable"
                clearable
              ></v-select>
            </div>

            <!-- Date -->
            <div class="input-block mb-3">
              <label class="col-form-label">Date <span class="text-danger">*</span></label>
              <div class="input-block form-focus position-relative">
                <div class="cal-icon" style="z-index: 90;">
                  <date-picker 
                    v-model="activityDate" 
                    placeholder="Select Date" 
                    class="form-control floating datetimepicker"
                    :editable="true" 
                    :clearable="false" 
                    :inputFormat="dateFormat" 
                  />
                </div>
              </div>
            </div>

            <!-- Time -->
            <div class="input-block mb-3">
              <label class="col-form-label">Time <span class="text-danger">*</span></label>
              <input type="text" maxlength="8" @keydown="handleTimeKeydown($event, $data, 'activityTime')" v-model="activityTime" class="form-control timepicker" placeholder="00:00:00" />
            </div>

            <!-- Location (Optional) -->
            <div class="input-block mb-3">
              <label class="col-form-label">Location (Optional)</label>
              <v-select
                v-model="locationId"
                :items="locationOptions"
                
                variant="outlined"
                density="comfortable"
                item-title="text"
                item-value="id"
                clearable
              ></v-select>
            </div>

            <!-- Reason -->
            <div class="input-block mb-3">
              <label class="col-form-label">Reason <span class="text-danger" v-if="!isAdmin">*</span></label>
              <textarea 
                v-model="reason" 
                class="form-control" 
                rows="2" 
                placeholder="Enter reason for manual activity"
              ></textarea>
            </div>

            <div class="submit-section">
              <button type="submit" class="btn btn-primary submit-btn" :disabled="loader">
                <span v-if="!loader">Add Activity</span>
                <div class="spinner-border spinner-border-sm text-light" role="status" v-else>
                  <span class="visually-hidden">Loading...</span>
                </div>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
  <!-- /Add Manual Activity Modal -->
</template>

<script>
import { timeInputMixin } from "../../mixins/timeInputMixin";
import axios from 'axios';
import { VUE_APP_API_BASE_URL } from "../../config";

import { getActivityTypeLabel } from '../../utils/util-funcitons';

export default {
  mixins: [timeInputMixin],
  emits: ['activityAdded'],
  props: {
    id: {
      type: String,
      default: 'add_manual_activity'
    }
  },
  data() {
    return {
      selectedEmployee: '',
      activityType: '',
      locationId: '',
      loader: false,
      locationOptions: [],
      employeeOptions: [],
      activityTypeOptions: ['Check In', 'Check Out', 'Break Out', 'Break In'],
      activityDate: null,
      activityTime: '00:00:00',
      dateFormat: 'dd/MM/yyyy',
      reason: ''
    };
  },

  computed: {
    // Combine date and time into activityDateTime string for API
    activityDateTime() {
      if (!this.activityDate) return '';
      const date = new Date(this.activityDate);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day} ${this.activityTime || '00:00:00'}`;
    },
    isAdmin() {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      return userInfo && userInfo.is_admin === 1;
    }
  },
  mounted() {
    this.getLocationIds();
    this.getEmployees();
  },
  methods: {
    getActivityTypeLabel,
    async getLocationIds() {
      try {
        const response = await axios.get(`${VUE_APP_API_BASE_URL}/location/all`);
        if (response.data.status) {
          this.locationOptions = response.data.data.map(element => ({
            id: element.id,
            text: element.name
          }));
        }
        this._applyPending();
      } catch (error) {
        console.error(error);
      }
    },

    async getEmployees() {
      try {
        const response = await axios.get(`${VUE_APP_API_BASE_URL}/user/user-list`);
        if (response.data.status) {
          this.employeeOptions = response.data.data.map(element => ({
            id: element.id,
            text: `${element.first_name} ${element.last_name}`
          }));
        }
        this._applyPending();
      } catch (error) {
        console.error(error);
      }
    },

    // Called by parent to pre-fill the form
    prefillValues({ employeeId, date, locationId, time }) {
      this.pendingPrefill = { employeeId, date, locationId, time };
      // Apply immediately if options are already loaded
      this._applyPending();
    },

    _applyPending() {
      if (!this.pendingPrefill) return;
      // Only wait for employee list to load (location is optional)
      if (!this.employeeOptions.length) return;
      const { employeeId, date, locationId, time } = this.pendingPrefill;
      if (employeeId) this.selectedEmployee = employeeId;
      if (date) {
        const parsedDate = new Date(date);
        this.activityDate = isNaN(parsedDate.getTime()) ? null : parsedDate;
      }
      if (time) this.activityTime = time;
      if (locationId != null) this.locationId = locationId;
      this.pendingPrefill = null;
    },

    submit() {
      if (!this.selectedEmployee) {
        this.$toast.error('Please select an employee');
        return;
      }
      if (!this.activityType) {
        this.$toast.error('Please select an activity type');
        return;
      }
      if (!this.activityDate) {
        this.$toast.error('Please select a date');
        return;
      }
      if (!this.isAdmin && (!this.reason || this.reason.trim() === '')) {
        this.$toast.error('Please enter a reason');
        return;
      }

      this.loader = true;
      
      const payload = {
        user_id: this.selectedEmployee,
        type: this.getActivityTypeLabel(this.activityType),
        reason: this.reason.trim()
      };

      // Add optional location if provided
      if (this.locationId) {
        payload.location_id = this.locationId;
      }

      // Add combined created_at
      if (this.activityDateTime) {
        payload.created_at = this.activityDateTime;
      }

      axios.post(`${VUE_APP_API_BASE_URL}/activity/add-manual`, payload)
        .then((response) => {
          if (response.data.status) {
            this.$toast.success(response.data.message || 'Activity added successfully');
            this.$refs.Close.click();
            this.$emit('activityAdded');
          } else {
            this.$toast.error(response.data.message || response.data.errors || 'Failed to add activity');
          }
        })
        .catch((error) => {
          const errorMessage = error.response?.data?.message || 
                              error.response?.data?.errors || 
                              'Something went wrong';
          this.$toast.error(errorMessage);
          console.error(error);
        })
        .finally(() => {
          this.loader = false;
          this.resetForm();
        });
    },

    resetForm() {
      this.selectedEmployee = '';
      this.activityType = '';
      this.locationId = '';
      this.activityDate = null;
      this.activityTime = '00:00:00';
      this.reason = '';

      this.pendingPrefill = null;
    }

  }
};
</script>

<style scoped>
/* Consistent styling for Ant Design date and time pickers */
:deep(.ant-picker) {
  width: 100%;
  height: 44px;
  border-radius: 4px;
  border: 1px solid #ced4da;
}

:deep(.ant-picker:hover),
:deep(.ant-picker-focused) {
  border-color: #86b7fe;
}

:deep(.ant-picker-input > input) {
  font-size: 14px;
}
</style>

